import { useQueryClient } from "@/hooks/useHttp"
import { useEffect, useMemo, useRef, useState } from "react"

import {
  LANGUAGE_LS,
  LAST_SUCCESS_TIME_LOG_IN,
  SLUG_LS
} from "@/const/local-storage.constants"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { rolePolicies } from "@/const/role-policies.constants"
import {
  getInitials,
  getLanguage,
  isUserAdmin,
  Permission,
  Role,
  User
} from "@/domain/user"
import { useLogger } from "@/hooks/useLogger"
import useServices from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { UserContext } from "@/hooks/useUser"
import TrialDaysExpired from "../TrialDaysExpired"

const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const client = useQueryClient()
  const { localStorage, auth, http, intercom } = useServices()
  const { logger, EVENTS } = useLogger()
  const { i18n } = useTranslation()
  const [user, setUser] = useState<User>(() => ({
    currentScopeId: http.getCurrentScopeId(),
    ...auth.getCurrentUser()
  }))
  const userExpired = useRef<(() => void) | undefined>()
  const value = useMemo(
    () => ({
      user,
      changeLanguage: (language: Settings.Language) => {
        i18n.changeLanguage(language)
        localStorage.setItem(LANGUAGE_LS, language)
      },
      updateFeatureFlags: (featureFlags: Partial<Features.Flags>) => {
        setUser((state: User) => ({
          ...state,
          featureFlags: {
            ...state.featureFlags,
            ...featureFlags
          }
        }))
      },
      setFeatureFlags: (
        featureFlags: Features.Response,
        role: User["role"],
        fullName: User["fullName"],
        settings: Settings.Shared
      ) => {
        const { dataReadiness, ...restFeatureFlags } = featureFlags
        const dataReadinessValue =
          dataReadiness === "digesting" ? dataReadiness : false

        setUser((state: User) => ({
          ...state,
          role,
          fullName,
          featureFlags: {
            ...state.featureFlags,
            ...restFeatureFlags,
            multiLanguage: getLanguage(state.company) !== "en",
            dataReadiness: dataReadinessValue
          },
          settings: {
            ...state.settings,
            ...settings
          }
        }))
      },
      signOut: async () => {
        try {
          await auth.signOut()
          http.reset()
          logger.reset()
          client.clear()
          intercom.shutdown()
          localStorage.removeItem(SLUG_LS)
          localStorage.removeItem(LAST_SUCCESS_TIME_LOG_IN)
          setUser(auth.getCurrentUser())
        } catch (err) {
          const message =
            err instanceof Error ? err.message : EVENTS.ERROR_EVENTS.SIGN_OUT

          logger.error(message)
          window.location.reload()
        }
      },
      setCurrentScope: (nextScope: string | null) => {
        setUser((state: User) => ({
          ...state,
          currentScopeId: nextScope
        }))
      },
      hasPermission: (permission: Permission): boolean | undefined => {
        if (!user.role) return undefined
        if (isUserAdmin(user)) return true
        const role: Role = user.role || "VIEWER"
        const policies = rolePolicies[role]
        if (!policies) return false
        const resourcePermissions = policies[permission.resource]
        if (!resourcePermissions) return false
        return resourcePermissions.some(
          (p) => p === "*" || p === permission.action
        )
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, auth, localStorage, logger, http]
  )

  useEffect(() => {
    auth.subscribe(
      auth.AUTH_STATE_CHANGED_EVENT,
      (currentUser: Partial<User>) => {
        if (currentUser.authorized) {
          logger.info(EVENTS.ANALYTIC_EVENTS.LOGIN, {
            email: currentUser.email,
            slug: currentUser.company
          })
        }

        if (!!http.isAuth && !currentUser.authorized) {
          http.isAuth = false
          http.post(RESOURCES_NAMES.AUTHENTICATION.LOGOUT, {}).catch((err) => {
            // "401 Unauthorized Error" is expected here if token is already expired otherwise log error
            if (err?.status !== 401)
              logger.error(err as Error, {
                custom_error: EVENTS.ERROR_EVENTS.SIGN_OUT
              })
          })
        }

        const currentLanguage = i18n.language
        const nextLanguage =
          localStorage.getItem(LANGUAGE_LS) || getLanguage(currentUser.company)

        if (currentLanguage !== nextLanguage) i18n.changeLanguage(nextLanguage)

        http.setSignOut(value.signOut)
        http.setFeatureFlags(value.setFeatureFlags)
        setUser((state: User) => ({
          ...state,
          ...currentUser,
          initials: getInitials(currentUser?.email || state?.email || "")
        }))
      }
    )

    auth.subscribe(
      auth.AUTH_MULTI_FACTOR_EVENT,
      (currentUser: Partial<User>) => {
        setUser((state: User) => ({
          ...state,
          ...currentUser
        }))
      }
    )

    auth.subscribe(auth.AUTH_TRIAL_DAYS_EXPIRED, (_: () => void) => {
      userExpired.current = _
      setUser((state: User) => ({
        ...state,
        featureFlags: { ...state.featureFlags, trialDays: -1 }
      }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isUserExpired = !!userExpired.current

  return (
    <UserContext.Provider value={value}>
      {!isUserExpired && children}
      {isUserExpired && (
        <TrialDaysExpired reject={userExpired}>{children}</TrialDaysExpired>
      )}
    </UserContext.Provider>
  )
}

export default UserProvider
